import { formatJson } from "./schemaOptimizerUtils";

function schemaLines(recommendedSchema) {
	return formatJson(recommendedSchema).split("\n");
}

function findLineIndex(lines, substr, from = 0) {
	if (!substr) return -1;
	for (let i = from; i < lines.length; i += 1) {
		if (lines[i].includes(substr)) return i;
	}
	return -1;
}

function blockEndLine(lines, startIdx) {
	const first = lines[startIdx] || "";
	const valueStart = first.indexOf("[") >= 0 ? first.indexOf("[") : first.indexOf("{");
	if (valueStart < 0) {
		return startIdx;
	}

	let depth = 0;
	for (let i = startIdx; i < lines.length; i += 1) {
		const line = lines[i];
		const from = i === startIdx ? valueStart : 0;
		for (let c = from; c < line.length; c += 1) {
			const ch = line[c];
			if (ch === "{" || ch === "[") depth += 1;
			else if (ch === "}" || ch === "]") depth -= 1;
		}
		if (depth <= 0 && (i > startIdx || from > 0)) return i;
	}
	return Math.min(startIdx + 80, lines.length - 1);
}

function findPropertyLine(lines, entry) {
	const property = entry?.property;
	if (!property) return -1;
	const needle = `"${property}"`;
	const nodeId = entry.nodeId;
	let searchFrom = 0;
	let searchTo = lines.length;

	if (nodeId) {
		const nodeStart = findLineIndex(lines, nodeId);
		if (nodeStart >= 0) {
			searchFrom = nodeStart;
			searchTo = Math.min(nodeStart + 500, lines.length);
		}
	}

	for (let i = searchFrom; i < searchTo; i += 1) {
		if (lines[i].includes(needle)) return i;
	}

	return findLineIndex(lines, needle);
}

function changeBlockRange(lines, entry) {
	const start = findPropertyLine(lines, entry);
	if (start < 0) return null;
	return { start, end: blockEndLine(lines, start) };
}

/** Line index (0-based in formatted recommended JSON) → change log entry index */
export function findChangeIndexByLineIndex(newLineIndex, recommendedSchema, changeLog) {
	if (newLineIndex == null || !changeLog?.length || !recommendedSchema) return null;
	const lines = schemaLines(recommendedSchema);
	let match = null;
	changeLog.forEach((entry, changeIndex) => {
		const range = changeBlockRange(lines, entry);
		if (!range) return;
		if (newLineIndex >= range.start && newLineIndex <= range.end) {
			match = changeIndex;
		}
	});
	return match;
}

/** Map recommended JSON line index (0-based) → change log entry index */
export function findChangeIndexForLine(lineText, changeLog, newLineIndex, recommendedSchema) {
	if (!lineText || !changeLog?.length) return null;

	if (newLineIndex != null && recommendedSchema) {
		const scoped = findChangeIndexByLineIndex(newLineIndex, recommendedSchema, changeLog);
		if (scoped != null) return scoped;
	}

	const propertyHits = [];
	for (let i = 0; i < changeLog.length; i += 1) {
		const entry = changeLog[i];
		if (entry?.property && lineText.includes(`"${entry.property}"`)) {
			propertyHits.push(i);
		}
	}
	if (propertyHits.length === 1) return propertyHits[0];
	if (propertyHits.length > 1 && newLineIndex != null && recommendedSchema) {
		const lines = schemaLines(recommendedSchema);
		for (const idx of propertyHits) {
			const range = changeBlockRange(lines, changeLog[idx]);
			if (range && newLineIndex >= range.start && newLineIndex <= range.end) return idx;
		}
	}
	return propertyHits[0] ?? null;
}

export function buildChangeLineMap(recommendedSchema, changeLog) {
	const map = new Map();
	if (!changeLog?.length || !recommendedSchema) return map;

	const lines = schemaLines(recommendedSchema);
	changeLog.forEach((entry, changeIndex) => {
		const propStart = findPropertyLine(lines, entry);
		if (propStart < 0) return;
		const end = blockEndLine(lines, propStart);
		for (let j = propStart; j <= end; j += 1) {
			map.set(j, changeIndex);
		}
	});
	return map;
}

export function resolveChangeIndexForRow(
	row,
	changeLineMap,
	changeLog,
	recommendedSchema
) {
	if (row.newLineIndex != null && changeLineMap.has(row.newLineIndex)) {
		return changeLineMap.get(row.newLineIndex);
	}
	if (row.newLineIndex != null && recommendedSchema) {
		const byIndex = findChangeIndexByLineIndex(
			row.newLineIndex,
			recommendedSchema,
			changeLog
		);
		if (byIndex != null) return byIndex;
	}
	const fromText = findChangeIndexForLine(
		row.text,
		changeLog,
		row.newLineIndex,
		recommendedSchema
	);
	if (fromText != null) return fromText;

	// Only added diff lines inherit a nearby property block (avoids tagging whole nodes).
	if (row.type === "added" && row.newLineIndex != null) {
		for (let d = 1; d <= 12; d += 1) {
			const prev = row.newLineIndex - d;
			const next = row.newLineIndex + d;
			if (changeLineMap.has(prev)) return changeLineMap.get(prev);
			if (changeLineMap.has(next)) return changeLineMap.get(next);
		}
	}

	return null;
}

/** Display line numbers (diff gutter) for a change — only lines that actually differ. */
export function displayLineRangeForChange(rowsWithChange, changeIndex, entry) {
	const related = rowsWithChange.filter((r) => r.changeIndex === changeIndex);
	const added = related.filter((r) => r.type === "added");
	if (added.length > 0) {
		const nums = added.map((r) => r.lineNum);
		return { start: Math.min(...nums), end: Math.max(...nums) };
	}

	const property = entry?.property;
	if (property) {
		const needle = `"${property}"`;
		const propRows = related.filter((r) => r.text.includes(needle));
		if (propRows.length > 0) {
			const nums = propRows.map((r) => r.lineNum);
			return { start: Math.min(...nums), end: Math.max(...nums) };
		}
	}

	if (related.length > 0) {
		const nums = related.map((r) => r.lineNum);
		return { start: Math.min(...nums), end: Math.max(...nums) };
	}

	return null;
}

export function changeEntryPath(entry) {
	if (!entry) return "";
	return [entry.nodeType, entry.property].filter(Boolean).join(" > ");
}

export function changeActionLabel(action) {
	const a = String(action || "").toLowerCase();
	if (a === "added") return "Added";
	if (a === "updated") return "Updated";
	if (a === "removed") return "Removed";
	if (a === "manual") return "Manual add required";
	return action || "Changed";
}

export function schemaSnippetForChange(recommendedSchema, entry) {
	if (!entry?.property || !recommendedSchema) return "";
	const lines = schemaLines(recommendedSchema);
	const start = findPropertyLine(lines, entry);
	if (start < 0) return "";
	const end = blockEndLine(lines, start);
	return lines.slice(start, end + 1).join("\n");
}

export function lineRangeLabel(startLine, endLine) {
	if (startLine == null) return "";
	if (endLine == null || endLine === startLine) return `Line ${startLine}`;
	return `Lines ${startLine}–${endLine}`;
}
